import { useMutation, useQuery } from '@tanstack/react-query';
import { api, apiEndpoints } from '../utils/apiService';
import { useScopedQueryKey } from './useScopedQueryKey';

export type SnoozeDuration =
  | 'until_close'
  | '30_minutes'
  | '1_hour'
  | '2_hours'
  | 'custom';

export interface MenuAvailabilityItem {
  id: number;
  name: string;
  price: number;
  base_available: boolean;
  is_snoozed: boolean;
  snooze_source: 'manual_dashboard' | 'provider' | null;
  snoozed_until: string | null;
  can_unsnooze: boolean;
}

export interface MenuAvailabilityCategory {
  id: number | string;
  name: string;
  items: MenuAvailabilityItem[];
}

export interface MenuAvailabilityData {
  categories: MenuAvailabilityCategory[];
  next_closing_time: string | null;
  timezone?: string;
  can_manage?: boolean;
}

export interface MenuAvailabilityChange {
  item_id: number;
  action: 'snooze' | 'unsnooze';
  duration?: SnoozeDuration;
  end_time?: string;
}

export interface MenuAvailabilityBulkPayload {
  changes: MenuAvailabilityChange[];
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === 'object' ? (value as UnknownRecord) : {};
}

function firstErrorMessage(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = firstErrorMessage(entry);
      if (message) return message;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const entry of Object.values(value as UnknownRecord)) {
      const message = firstErrorMessage(entry);
      if (message) return message;
    }
  }
  return null;
}

function normalizeItem(value: unknown): MenuAvailabilityItem {
  const item = asRecord(value);
  const snooze = asRecord(item.snooze);
  const source = item.snooze_source ?? snooze.source;
  return {
    id: Number(item.id),
    name: String(item.name ?? 'Unnamed item'),
    price: Number(item.price ?? 0),
    base_available: Boolean(
      item.base_available ?? item.provider_available ?? item.available ?? true,
    ),
    is_snoozed: Boolean(item.is_snoozed ?? item.snoozed ?? snooze.active),
    snooze_source:
      source === 'manual_dashboard' || source === 'provider' ? source : null,
    snoozed_until:
      typeof (item.snoozed_until ?? item.snooze_end ?? snooze.end_time) ===
      'string'
        ? String(item.snoozed_until ?? item.snooze_end ?? snooze.end_time)
        : null,
    can_unsnooze: Boolean(
      item.can_unsnooze ?? (source === 'manual_dashboard' || source == null),
    ),
  };
}

function normalizeAvailability(value: unknown): MenuAvailabilityData {
  const data = asRecord(value);
  const rawCategories = Array.isArray(data.categories)
    ? data.categories
    : Array.isArray(data.menus)
      ? data.menus.flatMap((rawMenu) => {
          const menu = asRecord(rawMenu);
          return Array.isArray(menu.categories) ? menu.categories : [];
        })
      : Array.isArray(value)
        ? value
        : [];

  return {
    categories: rawCategories.map((rawCategory, index) => {
      const category = asRecord(rawCategory);
      const rawItems = Array.isArray(category.items) ? category.items : [];
      return {
        id: (category.id as number | string | undefined) ?? index,
        name: String(category.name ?? 'Other'),
        items: rawItems.map(normalizeItem),
      };
    }),
    next_closing_time:
      typeof (data.next_closing_time ?? data.next_close) === 'string'
        ? String(data.next_closing_time ?? data.next_close)
        : null,
    timezone: typeof data.timezone === 'string' ? data.timezone : undefined,
    can_manage:
      typeof data.can_manage === 'boolean' ? data.can_manage : undefined,
  };
}

async function getErrorMessage(
  response: Response | null,
  fallback: string,
): Promise<string> {
  if (!response) return fallback;
  const body = await response.json().catch(() => null);
  const data = asRecord(body);
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;
  const nestedMessage = firstErrorMessage(body);
  if (nestedMessage) return nestedMessage;
  return fallback;
}

async function fetchMenuAvailability(): Promise<MenuAvailabilityData> {
  const response = await api.get(apiEndpoints.getMenuAvailability());
  if (!response?.ok) {
    throw new Error(
      await getErrorMessage(response, 'Failed to load menu availability.'),
    );
  }
  return normalizeAvailability(await response.json());
}

async function saveMenuAvailability(
  payload: MenuAvailabilityBulkPayload,
): Promise<MenuAvailabilityData | null> {
  const response = await api.post(
    apiEndpoints.updateMenuAvailabilityBulk(),
    payload,
  );
  if (!response?.ok) {
    throw new Error(
      await getErrorMessage(response, 'Failed to save menu availability.'),
    );
  }
  if (response.status === 204) return null;
  const body = await response.json().catch(() => null);
  return body ? normalizeAvailability(body) : null;
}

export function useMenuAvailabilityQuery() {
  const queryKey = useScopedQueryKey('menu-availability');
  return {
    queryKey,
    query: useQuery({
      queryKey,
      queryFn: fetchMenuAvailability,
    }),
    saveMutation: useMutation({
      mutationFn: saveMenuAvailability,
    }),
  };
}
